import { ReactNode, useEffect, useRef } from "react";
import { Modal, IconButton } from "@mui/material";

import "./style.scss";
import { ReactComponent as CloseIcon } from "../../assets/cross.icon.svg";

export const ModalTemplate = ({
    children,
    open,
    width,
    height,
    showClose = true,
    ariaLabel,
    handleClose
}: {
    children: ReactNode;
    open: boolean;
    width?: number;
    height?: number;
    showClose?: boolean;
    ariaLabel?: string;
    handleClose?: (arg1: boolean) => void;
}) => {
    const style = { ...(width ? { maxWidth: width } : {}), ...(height ? { height: height } : {}) };

    // MUI Modal's built-in focus restore does not fire for these dialogs (focus
    // drops to <body> on close, so keyboard users lose their place — WCAG 2.4.3).
    // Restore it explicitly: capture the opener during render — before the Modal
    // child mounts and moves focus into the dialog — and return focus to it once
    // the modal closes. requestAnimationFrame defers the focus past the modal's
    // teardown so it isn't immediately reset to <body>.
    const triggerRef = useRef<HTMLElement | null>(null);
    const wasOpenRef = useRef(false);
    if (open && !wasOpenRef.current) {
        const opener = document.activeElement as HTMLElement | null;
        // When the modal is opened from a menu, the active element is a menu
        // item that unmounts with the menu — restoring focus to it would fail.
        // Resolve to the menu's trigger instead (the menu list's aria-labelledby
        // points to the trigger's id), so focus returns to the kebab/button.
        const menuList = opener?.closest('[role="menu"]');
        const labelledBy = menuList?.getAttribute("aria-labelledby");
        const trigger = labelledBy ? document.getElementById(labelledBy) : null;
        triggerRef.current = trigger ?? opener;
    }
    wasOpenRef.current = open;

    useEffect(() => {
        if (!open && triggerRef.current) {
            const trigger = triggerRef.current;
            triggerRef.current = null;
            requestAnimationFrame(() => {
                if (trigger.isConnected) {
                    trigger.focus();
                }
            });
        }
    }, [open]);

    const handleCloseClick = () => {
        if (handleClose) {
            handleClose(false);
        }
    };

    return (
        // onClose (Escape / backdrop) is only wired when the modal is
        // dismissable (showClose), so intentionally sticky modals stay put.
        <Modal
            className="modal-template"
            open={open}
            onClose={showClose ? handleCloseClick : undefined}
        >
            <div
                className="modal-template-view"
                style={style}
                // Only expose dialog semantics when we have an accessible name;
                // a role="dialog" without a name is itself a violation, so
                // consumers opt in by passing ariaLabel.
                role={ariaLabel ? "dialog" : undefined}
                aria-modal={ariaLabel ? "true" : undefined}
                aria-label={ariaLabel}
            >
                {showClose && (
                    <IconButton
                        className="modal-template-close-button"
                        aria-label="Close"
                        onClick={handleCloseClick}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                {children}
            </div>
        </Modal>
    );
};
